import { useMutation } from '@apollo/client';
import type { InsertDocumentOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDocumentOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertDocumentOversightResultInput } from './useInsertDocumentOversightResultTRPC';
import { useInsertDocumentOversightResultTRPC } from './useInsertDocumentOversightResultTRPC';

export const useInsertDocumentOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertDocumentOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'document_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertDocumentOversightResultTRPC();

  const insertDocumentOversightResult = async (
    variables: InsertDocumentOversightResultInput
  ): Promise<InsertDocumentOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDocumentOversightResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert document oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDocumentOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDocumentOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
